import {
  Title,
  Legend,
  Tooltip,
  ChartData,
  LinearScale,
  LineElement,
  ChartOptions,
  PointElement,
  CategoryScale,
  Chart as ChartJS
} from "chart.js";
import { Line } from "react-chartjs-2";
import React, { useState, useEffect } from "react";

import {
  WidgetTitle,
  PrimaryStatsInfo,
  SecondaryStatsInfo,
  WidgetHeaderContent,
  AnalyticsWidgetHeader,
  AnalyticsContentWrapper,
  AnalyticsWidgetContainer
} from "../styles";
import { colors } from "theme";
import ImpressionsTooltip from "./Tooltip";
import Loader from "components/common/loading/Loader";
import { getNumWithMaxDecimals } from "utils/numbers";
import { useDashboardState } from "contextApi/dashboardContext";
import { getChartData, getLineChartOptions } from "utils/charts";
import { today, difference, subtractDuration } from "utils/date";
import { externalTooltipHandler } from "../ExternalTooltipHandler";

ChartJS.register(
  Title,
  Legend,
  Tooltip,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale
);

interface IChartConfig {
  chartData: ChartData<"line">;
  chartOptions: ChartOptions<"line">;
}

const ImpressionsAnalytics: React.FC<{}> = () => {
  const filterRange = useDashboardState(state => state.filterRange);
  const stats = useDashboardState(state => state.stats.data);
  const isFetching = useDashboardState(state => state.stats.isFetching);
  const minDataPoints: null | number =
    typeof filterRange.periodInDays === "number"
      ? difference(
          today(),
          subtractDuration(today(), {
            days: filterRange.periodInDays
          }),
          "weeks"
        )
      : null;
  const [{ chartData, chartOptions }, setChartConfig] = useState<IChartConfig>({
    chartData: { labels: [], datasets: [] },
    chartOptions: getLineChartOptions([], {
      scales: { y: { display: false }, x: { display: false } }
    })
  });

  useEffect(() => {
    if (!isFetching && !!stats?.impressions?.points) {
      const formattedData = stats.impressions.points.map((point: any) => ({
        ...point,
        total: point.variation
      }));
      const { data, dates } = getChartData(
        formattedData,
        true,
        formattedData.length >= 1 && minDataPoints ? minDataPoints : 9
      );
      const options = getLineChartOptions(data, {
        ...(stats.impressions.points.length < 1
          ? {
              scales: {
                y: {
                  ticks: {
                    display: false
                  }
                }
              }
            }
          : {}),
        elements: {
          point: { radius: stats.impressions.points.length === 1 ? 3 : 0 }
        },
        plugins: {
          tooltip: {
            enabled: false,
            external: externalTooltipHandler(ImpressionsTooltip)
          }
        }
      });

      setChartConfig({
        chartData: {
          labels: dates,
          datasets: [
            {
              data
            }
          ]
        },
        chartOptions: options
      });
    }
  }, [isFetching, minDataPoints, stats]);

  const progressPercentage = (() => {
    if (
      !stats?.impressions ||
      typeof stats.impressions.previous !== "number" ||
      typeof stats.impressions.period !== "number"
    ) {
      return null;
    } else {
      if (stats.impressions.previous === 0) return null;

      const diffPercentage =
        ((stats.impressions.period - stats.impressions.previous) /
          stats.impressions.previous) *
        100;

      if (Number.isFinite(diffPercentage)) {
        return getNumWithMaxDecimals(diffPercentage, 1);
      } else {
        return 0;
      }
    }
  })();

  return (
    <AnalyticsWidgetContainer
      colspan={1}
      rowspan={1}
      bgColor={colors.deepOrange}
    >
      <AnalyticsWidgetHeader>
        <WidgetTitle className="alternate">Impressions</WidgetTitle>
        {!isFetching && (
          <WidgetHeaderContent>
            <PrimaryStatsInfo>
              {!!stats?.impressions &&
              typeof stats.impressions.period === "number" &&
              !!stats?.impressions?.points &&
              stats.impressions.points.length >= 1
                ? stats.impressions.period.toString().length >= 5
                  ? `${getNumWithMaxDecimals(
                      stats.impressions.period / 1000,
                      1
                    )}k`
                  : stats.impressions.period
                : "-"}
            </PrimaryStatsInfo>
            {progressPercentage !== null &&
              !!stats?.impressions?.points &&
              stats.impressions.points.length >= 1 && (
                <SecondaryStatsInfo>
                  {progressPercentage < 0 ? "-" : "+"}
                  {Math.abs(progressPercentage)}%
                </SecondaryStatsInfo>
              )}
          </WidgetHeaderContent>
        )}
      </AnalyticsWidgetHeader>
      <AnalyticsContentWrapper>
        {isFetching ? (
          <Loader size={36} />
        ) : (
          <Line
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        )}
        {!isFetching &&
          (!stats?.impressions?.points ||
            stats.impressions.points.length < 1) && (
            <span className="no-data">Not enough data</span>
          )}
      </AnalyticsContentWrapper>
    </AnalyticsWidgetContainer>
  );
};

export default ImpressionsAnalytics;
